import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

import { PauseIcon, PlayIcon } from '../components/icons'
import { stopAllSpeech } from '../lib/speech'

/**
 * Sesli mesaj oynaticisi (mesaj balonunun icinde).
 *
 * ## Sure SUNUCUDAN
 *
 * Tarayicinin `MediaRecorder`i webm dosyasina sure yazmiyor: `<audio>`
 * `duration` icin `Infinity` veriyor ve yerel oynatici "0:00 / 0:00"
 * gosteriyordu. Sure transkripsiyonda olculuyor ve ekle birlikte geliyor;
 * yalnizca yoksa (eski kayit) tarayicinin bildirdigine dusuluyor.
 *
 * ## Ayni anda TEK ses
 *
 * Bir sesli mesaj calmaya baslayinca oncekisi ve (varsa) sesli yanit
 * okumasi duruyor - iki sesin ust uste calmasi anlasilmaz.
 *
 * Renkler `currentColor`dan: ayni bilesen kullanici balonunda (beyaz metin,
 * degrade zemin) ve paylasim ekraninda (tema metni) ayarsiz calisiyor.
 */

let playing: HTMLAudioElement | null = null

export function VoiceNote({ src, duration }: { src: string; duration?: number | null }) {
  const { t } = useTranslation()
  const audio = useRef<HTMLAudioElement>(null)
  const [isPlaying, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [measured, setMeasured] = useState<number | null>(null)
  const [failed, setFailed] = useState(false)

  const total = duration && duration > 0 ? duration : (measured ?? 0)

  useEffect(() => {
    const element = audio.current
    return () => {
      if (element && playing === element) playing = null
      element?.pause()
    }
  }, [])

  async function toggle() {
    const element = audio.current
    if (!element) return
    if (!element.paused) {
      element.pause()
      return
    }
    if (playing && playing !== element) playing.pause()
    stopAllSpeech()
    playing = element
    // Sona gelmis bir kayit bastan calsin.
    if (total && element.currentTime >= total - 0.05) element.currentTime = 0
    try {
      await element.play()
    } catch {
      setFailed(true)
    }
  }

  function seek(value: number) {
    const element = audio.current
    if (!element) return
    element.currentTime = value
    setPosition(value)
  }

  const progress = total ? Math.min(100, (position / total) * 100) : 0

  return (
    <div className={isPlaying ? 'voice-note playing' : 'voice-note'}>
      <button
        type="button"
        className="voice-note-btn"
        onClick={() => void toggle()}
        aria-label={isPlaying ? t('voice.note.pause') : t('voice.note.play')}
        title={isPlaying ? t('voice.note.pause') : t('voice.note.play')}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </button>
      <input
        type="range"
        className="voice-note-track"
        min={0}
        max={total || 1}
        step={0.05}
        value={Math.min(position, total || 1)}
        onChange={(event) => seek(Number(event.target.value))}
        disabled={!total}
        aria-label={t('voice.note.position')}
        aria-valuetext={`${formatClock(position)} / ${formatClock(total)}`}
        style={{ '--voice-progress': `${progress}%` } as CSSProperties}
      />
      <span className="voice-note-time">
        {isPlaying || position > 0 ? formatClock(position) : formatClock(total)}
      </span>
      {failed && (
        <span className="voice-note-error" role="alert">
          {t('voice.note.failed')}
        </span>
      )}
      <audio
        ref={audio}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setPosition(0)
          if (playing === audio.current) playing = null
        }}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const value = event.currentTarget.duration
          if (Number.isFinite(value) && value > 0) setMeasured(value)
        }}
        onError={() => setFailed(true)}
      />
    </div>
  )
}

/** 0:07, 1:05 - sesli mesajlar dakikalarca surmuyor, saat gerekmez. */
export function formatClock(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0
  const minutes = Math.floor(safe / 60)
  return `${minutes}:${String(safe % 60).padStart(2, '0')}`
}

/**
 * Sesli mesajin transkripti - KAPALI gelir, istenince acilir.
 *
 * Transkript kullanicinin YAZDIGI bir metin degil, otomatik ceviri (ve
 * duyma hatasi icerebilir). Acik haliyle normal mesaj metni gibi durunca
 * kullanici onu yazdigi bir sey sandi (kullanici geri bildirimi). Bu yuzden:
 * varsayilan kapali, acikken de alinti gibi - kucuk, soluk, sol cizgili,
 * markdown olarak DEGIL duz metin olarak.
 */
export function VoiceTranscript({ text, id }: { text: string; id: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const panel = `voice-transcript-${id}`
  if (!text.trim()) return null
  return (
    <div className={open ? 'voice-transcript open' : 'voice-transcript'}>
      <button
        type="button"
        className="voice-transcript-toggle"
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen((value) => !value)}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m9 6 6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {open ? t('voice.note.hideTranscript') : t('voice.note.showTranscript')}
      </button>
      {open && (
        <blockquote id={panel} className="voice-transcript-text">
          {text}
        </blockquote>
      )}
    </div>
  )
}
